import { app, TransitionConfig } from './core/App';
import { SceneAssets } from './core/modules/AssetManager';
import { RunSettings } from './game/RunSettingsHandler';
import { assetPackLookup, AssetPack, SpineAssets, audioLookup, buildSpineLookup } from './assets-to-load';

import Boot from './scenes/Boot';
import Title from './scenes/Title';
import PreloadCartoonito from './scenes/PreloadCartoonito';
import LevelSelect from './scenes/LevelSelect';
import Game from './scenes/Game';
import TransitionSceneToScene from './scenes/TransitionSceneToScene';
import GameUI from './scenes/GameUI';
import DebugUI from './scenes/DebugUI';
import PreloadBatwheels from './scenes/PreloadBatwheels';
import TransitionLoadScene from './scenes/TransitionLoadScene';
import Intro from './scenes/Intro';

export const SceneId = {
  Boot: 'Boot',
  PreloadCartoonito: 'PreloadCartoonito',
  PreloadTomAndJerry: 'PreloadTomAndJerry',
  PreloadBatwheels: 'PreloadBatwheels',
  Title: 'Title',
  Intro: 'Intro',
  LevelSelect: 'LevelSelect',
  Game: 'Game',
  GameUI: 'GameUI',
  TransitionSceneToScene: 'TransitionSceneToScene',
  TransitionLoadScene: 'TransitionLoadScene',
  DebugUI: 'DebugUI'
} as const;

export const TransitionPoint = {
  BootEnd: 'BootEnd',
  PreloadCartoonitoEnd: 'PreloadCartoonitoEnd',
  PreloadEnd: 'PreloadEnd',
  TitleEnd: 'TitleEnd',
  IntroBack: 'IntroBack',
  IntroEnd: 'IntroEnd',
  LevelSelectBack: 'LevelSelectBack',
  LevelSelectEnd: 'LevelSelectEnd',
  GameBack: 'GameBack',
  GameHome: 'GameHome',
  GameLevelSelect: 'GameLevelSelect',
  GameReplay: 'GameReplay'
} as const;

export const scenes = [Boot, PreloadBatwheels, Title, Intro, LevelSelect, Game, GameUI, TransitionSceneToScene, TransitionLoadScene, DebugUI];

// Scene Asset Dependencies.
const baseAssetPacks: Set<string> = new Set([AssetPack.Common]);
const sceneAssetData: Map<keyof typeof SceneId, SceneAssets> = new Map();
sceneAssetData.set(SceneId.Title, { requiredAssetPacks: [AssetPack.Title] });
sceneAssetData.set(SceneId.Intro, { requiredAssetPacks: [AssetPack.Intro] });
sceneAssetData.set(SceneId.LevelSelect, { requiredAssetPacks: [AssetPack.LevelSelect], requiredSpineAssets: [SpineAssets.BatComputer] });
sceneAssetData.set(SceneId.Game, { hasDynamicAssets: true, requiredAssetPacks: [AssetPack.Game, AssetPack.GameSky] });

// Routing table.
const sceneMap: Map<keyof typeof TransitionPoint, keyof typeof SceneId | TransitionConfig> = new Map();
// sceneMap.set(TransitionPoint.BootEnd, SceneId.PreloadCartoonito);
sceneMap.set(TransitionPoint.BootEnd, SceneId.PreloadBatwheels);
sceneMap.set(TransitionPoint.PreloadEnd, SceneId.Title);
sceneMap.set(TransitionPoint.TitleEnd, {
  nextScene: SceneId.Intro,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.IntroBack, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.IntroEnd, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectBack, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectEnd, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameBack, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameHome, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameLevelSelect, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameReplay, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionSceneToScene,
  transitionTime: 1000,
  waitForPreload: true
});

app.updateSceneAssetMaps(assetPackLookup, buildSpineLookup(), audioLookup, baseAssetPacks, sceneAssetData);
app.updateSceneMap(sceneMap as unknown as Map<string, string>, TransitionPoint.PreloadEnd.toString());

export interface SceneData {
  sourceScene: keyof typeof SceneId;
  sourceTransition?: keyof typeof TransitionPoint;
  transitionData?: TransitionSceneData;
  gameSceneData?: GameSceneData;
  runSettings?: Partial<RunSettings>;
  [key: string]: any;
}

export interface TransitionSceneData {
  sourceScene: string;
  nextScene: string;
}

export interface GameSceneData {
  shouldSkip: boolean;
}
